import capitalize from 'lodash/capitalize';
import { bindToggle } from 'material-ui-popup-state/core';
import { bindPopover, usePopupState } from 'material-ui-popup-state/hooks';

import {
  Icons,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  withProps,
  tokens,
} from '@cast/design-system';

import { ProfileAvatar } from '@oneclick/components/avatars/ProfileAvatar';
import { Link as RouterLink, LogoutWrapper } from 'components/router';
import { getDocsLink } from '@cast/utils';

import { BASE_PATHS } from '@oneclick/constants/navigation';

import { HeaderIconButton } from './HeaderIconButton';
import { HeaderPopover } from './HeaderPopover';

type ProfileMenuProps = {
  user: string;
  email: string;
};

const ProfileListItemText = withProps(ListItemText, {
  primaryTypographyProps: {
    variant: 'P12M',
    color: tokens.theme.fg.structural.primary,
  },
  secondaryTypographyProps: {
    variant: 'P10R',
    color: tokens.theme.fg.structural.secondary,
  },
});

export const ProfileMenu = ({ user, email }: ProfileMenuProps) => {
  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'profile-menu',
  });

  return (
    <>
      <HeaderIconButton
        {...bindToggle(popupState)}
        active={popupState.isOpen}
        aria-label="Profile menu"
        data-testid="profile-menu-btn"
      >
        <ProfileAvatar name={user} email={email} size="sm" />
      </HeaderIconButton>
      <HeaderPopover {...bindPopover(popupState)} testId="profile-menu">
        <List size="medium" disablePadding sx={{ minWidth: '190px' }}>
          <ListItem
            startAdornment={
              <ListItemIcon>
                <ProfileAvatar
                  name={user}
                  email={email}
                  size="md"
                  sx={{ cursor: 'default' }}
                />
              </ListItemIcon>
            }
            sx={{
              '&, &:hover': {
                borderBottom: tokens.theme.border.structural.neutralSubtle,
                backgroundColor: tokens.theme.bg.action.surfaceHover,
                backgroundImage: 'none',
              },
              cursor: 'default',
            }}
            multiline
            disableHover
          >
            <ProfileListItemText primary={capitalize(user)} secondary={email} />
          </ListItem>
          <ListItem
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            component={RouterLink}
            testId="api-keys-link"
            to={BASE_PATHS.apiAccess}
            startAdornment={
              <Icons.Key
                weight="regular"
                color={tokens.theme.fg.structural.primary}
              />
            }
            onClick={() => popupState.close()}
          >
            <ListItemText>API keys</ListItemText>
          </ListItem>
          <ListItem
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            component="a"
            testId="api-documentation-link"
            target="_blank"
            rel="noreferrer noopener"
            href={getDocsLink('castai_swagger')}
            startAdornment={
              <Icons.Book
                weight="regular"
                color={tokens.theme.fg.structural.primary}
              />
            }
            onClick={() => popupState.close()}
          >
            <ListItemText>API documentation</ListItemText>
          </ListItem>

          <LogoutWrapper>
            <ListItem
              testId="log-out-btn"
              sx={{ color: tokens.theme.fg.status.error }}
              startAdornment={
                <Icons.SignOut
                  weight="regular"
                  color={tokens.theme.fg.status.error}
                />
              }
            >
              <ListItemText>Log out</ListItemText>
            </ListItem>
          </LogoutWrapper>
        </List>
      </HeaderPopover>
    </>
  );
};
